/**
 * Detects that this tab is running a bundle an earlier deploy produced.
 *
 * `__BUILD_ID__` is injected by vite.config.ts and changes with every build,
 * so comparing it against the freshly fetched /version.json is the one
 * reliable "you are stale" signal. Everything else (service worker events,
 * chunk load failures) is treated as a hint that is verified against it.
 */

import { isChunkLoadError } from './errorClassification';
import { safeGetJSON, safeSetJSON } from '../utils/safeStorage';

declare const __BUILD_ID__: string;

export const BUILD_ID: string = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'development';

export const IS_DEVELOPMENT = BUILD_ID === 'development';

let isOutdated = false;
let versionMismatchCallbacks: Array<() => void> = [];
let lastCheckedVersion: string | null = null;

export function onVersionMismatch(callback: () => void): () => void {
  versionMismatchCallbacks.push(callback);
  return () => {
    versionMismatchCallbacks = versionMismatchCallbacks.filter((cb) => cb !== callback);
  };
}

function notifyVersionMismatch(): void {
  if (IS_DEVELOPMENT) return;
  isOutdated = true;
  versionMismatchCallbacks.forEach((cb) => {
    try {
      cb();
    } catch (error) {
      console.error('[Version] Error in mismatch callback:', error);
    }
  });
}

export function getIsOutdated(): boolean {
  if (IS_DEVELOPMENT) return false;
  return isOutdated;
}

export async function checkServerVersion(): Promise<boolean> {
  if (IS_DEVELOPMENT) return false;

  try {
    const response = await fetch(`/version.json?_t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      console.warn('[Version] Could not fetch version.json:', response.status);
      return false;
    }

    const data = await response.json();
    const serverVersion = data?.buildId;
    lastCheckedVersion = serverVersion ?? null;

    if (serverVersion && serverVersion !== BUILD_ID) {
      notifyVersionMismatch();
      return true;
    }

    return false;
  } catch (error) {
    console.warn('[Version] Error checking version:', error);
    return false;
  }
}

/**
 * Returns true when the failure was a stale deploy the pipeline now owns.
 * A chunk error whose server version still matches is left alone: that is a
 * transient network or CDN problem and forcing an update prompt would trap
 * the visitor in a reload loop.
 */
export async function handleChunkLoadError(error: Error): Promise<boolean> {
  if (!isChunkLoadError(error)) return false;
  if (IS_DEVELOPMENT) return false;

  return await checkServerVersion();
}

export function markAsOutdated(): void {
  if (IS_DEVELOPMENT) return;
  if (!isOutdated) notifyVersionMismatch();
}

export function forceRefresh(): void {
  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  // Clear the service worker caches first so the reload cannot be served the
  // old shell, but never let a hung cache API strand the visitor.
  if (typeof caches !== 'undefined') {
    window.setTimeout(reload, 2000);
    void caches
      .keys()
      .then((names) => Promise.all(names.map((name) => caches.delete(name))))
      .catch((error) => {
        console.warn('[Version] Failed to clear caches before refresh:', error);
      })
      .finally(() => {
        reload();
      });
    return;
  }

  reload();
}

const STALE_RELOAD_GUARD_KEY = 'truevined_stale_reload_guard';
const STALE_RELOAD_WINDOW_MS = 60_000;

/**
 * Hard-reload a stale tab exactly once per (build, path) so the browser picks
 * up the fresh deploy and lands on the page the visitor was already heading
 * to. Returns false when the reload was already attempted recently, so the
 * caller can fall back to the manual prompt instead of looping.
 */
export function attemptStaleRecoveryReload(): boolean {
  if (IS_DEVELOPMENT || typeof window === 'undefined') return false;

  const guardKey = `${BUILD_ID}:${window.location.pathname}`;
  const last = safeGetJSON<{ key?: string; at?: number }>(STALE_RELOAD_GUARD_KEY, 'session');
  if (
    last &&
    last.key === guardKey &&
    typeof last.at === 'number' &&
    Date.now() - last.at < STALE_RELOAD_WINDOW_MS
  ) {
    return false;
  }
  if (!safeSetJSON(STALE_RELOAD_GUARD_KEY, { key: guardKey, at: Date.now() }, 'session')) {
    return false;
  }

  window.setTimeout(() => window.location.reload(), 50);
  return true;
}

export function getVersionInfo(): { buildId: string; isOutdated: boolean; lastChecked: string | null } {
  return { buildId: BUILD_ID, isOutdated, lastChecked: lastCheckedVersion };
}
